import React from 'react';
import PropTypes from 'prop-types';
import { noop } from 'node-noop';
import classnames from 'classnames';

import { processInputLinkProp, encodeLinkBeforeSave } from '../../../utils/url-utils';
import LocalizationConsumer from '../../../l10n/localization-context';
import InsertLink from '../../insert-link';
import ImageSelector from './components/image-selector';
import Modal from '../../modal';

const stateKeys = {
  classicStateKey: 'classicState',
  imagesSettingsStateKey: 'imagesSettingsState'
};

class InsertLinkDialog extends React.Component
{
  static propTypes =
  {
    isSlideShowInsertLinksMode: PropTypes.bool,
    isSimpleView: PropTypes.bool,
    isEditMode: PropTypes.bool,
    onCancel: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onUnlink: PropTypes.func,
    tooltip: PropTypes.string,
    linkText: PropTypes.string,
    url: PropTypes.string,
    isLinkTextEditable: PropTypes.bool,
    openInNewWindow: PropTypes.bool,
    previewImage: PropTypes.string,
    baseDetailsUrlTemplate: PropTypes.string.isRequired,
    baseRegistrationUrlTemplate: PropTypes.string.isRequired,
    eventsEndpoint: PropTypes.string.isRequired,
    pagesStructureEndpoint: PropTypes.string.isRequired,
    isLinkTextSectionHidden: PropTypes.bool,
    isLinkStyleSectionHidden: PropTypes.bool,
    isLinkOptionsHidden: PropTypes.bool,
    isTooltipOptionHidden: PropTypes.bool,
    linkStyles: PropTypes.arrayOf(PropTypes.string),
    linkStyle: PropTypes.string,
    linkStyleIframeSrc: PropTypes.string,
    imagesSettings: PropTypes.shape({}),
    slideShowImageDescription: PropTypes.string
  };

  static defaultProps =
  {
    isSlideShowInsertLinksMode: false,
    isSimpleView: false,
    isEditMode: false,
    tooltip: null,
    linkText: null,
    url: '',
    isLinkTextEditable: true,
    openInNewWindow: false,
    previewImage: '',
    isLinkTextSectionHidden: false,
    isLinkStyleSectionHidden: false,
    isTooltipOptionHidden: false,
    isLinkOptionsHidden: false,
    linkStyles: [],
    linkStyle: '',
    linkStyleIframeSrc: '',
    onUnlink: noop,
    imagesSettings: {},
    slideShowImageDescription: ''
  };

  constructor(props)
  {
    super(props);

    const {
      tooltip,
      linkText,
      url,
      openInNewWindow,
      linkStyle,
      slideShowImageDescription,
      imagesSettings
    } = this.props;

    this.state = {
      [stateKeys.classicStateKey]: {
        tooltip,
        linkText,
        slideShowImageDescription,
        url: processInputLinkProp(url),
        openInNewWindow,
        linkStyle
      },
      [stateKeys.imagesSettingsStateKey]: imagesSettings,
      activeSlideShowImage: Object.keys(imagesSettings)[0] || null
    };
  }

  getButtons = () =>
  {
    const {
      onCancel,
      onUnlink,
      isEditMode,
      isSlideShowInsertLinksMode
    } = this.props;

    const { url } = this.state[stateKeys.classicStateKey];

    const isSaveButtonDisabled = isSlideShowInsertLinksMode ? false : !url.trim();

    const getSaveButtonName = () =>
    {
      if (isSlideShowInsertLinksMode) return 'insert-link-dialog.button-insert.slideshow';
      if (isEditMode) return 'insert-link-dialog.button-edit';
      return 'insert-link-dialog.button-insert-link';
    };


    const buttons = [
      {
        name: <LocalizationConsumer id={getSaveButtonName()} />,
        buttonHandler: this.savePayload,
        isDisabled: isSaveButtonDisabled
      },
      {
        name: <LocalizationConsumer id="insert-link-dialog.button-cancel" />,
        buttonHandler: onCancel
      }
    ];

    if (isEditMode)
    {
      const unlinkButton =
        {
          name: <LocalizationConsumer id="insert-link-dialog.button-unlink" />,
          buttonHandler: onUnlink
        };

      buttons.splice(1, 0, unlinkButton);
    }

    return buttons;
  };

  setActiveSlideShowImage = (image) =>
  {
    this.setState({ activeSlideShowImage: image });
  };

  setSettingState = (key, value) =>
  {
    const { isSlideShowInsertLinksMode } = this.props;
    const { activeSlideShowImage } = this.state;

    if (isSlideShowInsertLinksMode)
    {
      this.setState(state => ({ [stateKeys.imagesSettingsStateKey]:
          ({ ...state[stateKeys.imagesSettingsStateKey],
            [activeSlideShowImage]:
            { ...state[stateKeys.imagesSettingsStateKey][activeSlideShowImage], [key]: value } }) }));
    } else
    {
      this.setState(state => ({ [stateKeys.classicStateKey]: { ...state[stateKeys.classicStateKey], [key]: value } }));
    }
  };

  getSettingsState = () =>
  {
    const { isSlideShowInsertLinksMode } = this.props;
    const { activeSlideShowImage } = this.state;

    return isSlideShowInsertLinksMode ?
      this.state[stateKeys.imagesSettingsStateKey][activeSlideShowImage] : this.state[stateKeys.classicStateKey];
  };

  savePayload = () =>
  {
    const { onSave, isSlideShowInsertLinksMode } = this.props;

    if (isSlideShowInsertLinksMode)
    {
      onSave(this.state[stateKeys.imagesSettingsStateKey]);
      return;
    }

    const {
      tooltip,
      linkText,
      url,
      openInNewWindow,
      linkStyle,
      slideShowImageDescription
    } = this.state[stateKeys.classicStateKey];

    onSave({
      tooltip,
      linkText,
      url: encodeLinkBeforeSave(url),
      openInNewWindow,
      slideShowImageDescription,
      linkStyle
    });
  };

  handleSlideShowImageDescriptionChange = (description) =>
  {
    this.setSettingState('slideShowImageDescription', description);
  };

  handleUrlChange = (url) =>
  {
    this.setSettingState('url', url);
  };

  handleLinkTextChange = (linkText) =>
  {
    const { isLinkTextEditable } = this.props;

    if (isLinkTextEditable)
    {
      this.setSettingState('linkText', linkText);
    }
  };

  handleTooltipChange = (tooltip) =>
  {
    this.setSettingState('tooltip', tooltip);
  };

  handleOpenInNewWindowChange = (e) =>
  {
    const openInNewWindow = e.target.value === 'true';

    this.setSettingState('openInNewWindow', openInNewWindow);
  };

  handleLinkStyleChange = (linkStyle) =>
  {
    this.setSettingState('linkStyle', linkStyle);
  };

  render()
  {
    const {
      isSlideShowInsertLinksMode,
      isSimpleView,
      isEditMode,
      onCancel,
      isLinkTextEditable,
      previewImage,
      baseDetailsUrlTemplate,
      baseRegistrationUrlTemplate,
      eventsEndpoint,
      pagesStructureEndpoint,
      isLinkTextSectionHidden,
      isLinkStyleSectionHidden,
      isTooltipOptionHidden,
      isLinkOptionsHidden,
      linkStyles,
      linkStyleIframeSrc
    } = this.props;

    const settingsState = this.getSettingsState();

    const isTallMode = !!(previewImage && !isLinkStyleSectionHidden
      && !isLinkOptionsHidden && !isTooltipOptionHidden && !isSlideShowInsertLinksMode);

    const {
      tooltip,
      linkText,
      url,
      openInNewWindow,
      slideShowImageDescription,
      linkStyle
    } = settingsState;


    const { activeSlideShowImage } = this.state;

    const getModalTitleId = () =>
    {
      if (isSlideShowInsertLinksMode) return 'insert-link-dialog.header.slideshow';
      if (isEditMode) return 'insert-link-dialog.header.edit';
      return 'insert-link-dialog.header.insert';
    };

    const modalContentContainerClasses = classnames({
      '-wa-edit-controls__insert-link-container-holder': true,
      '-wa-edit-controls__insert-link-container-holder__slideshow': isSlideShowInsertLinksMode,
      '-wa-edit-controls__insert-link-container-holder__tall-mode': isTallMode
    });

    const getModalWidth = () => (isSlideShowInsertLinksMode) ? 1000 : 765;


    return (
      <Modal
        onCloseDialogClick={onCancel}
        size={{ width: getModalWidth() }}
        withPadding
        withoutHeightCalc
      >
        <Modal.Header>
          <LocalizationConsumer id={getModalTitleId()} />
        </Modal.Header>
        <Modal.Content>
          <div className={modalContentContainerClasses}>
            { isSlideShowInsertLinksMode ?
              <ImageSelector
                images={Object.keys(this.state[stateKeys.imagesSettingsStateKey])}
                activeImage={activeSlideShowImage}
                onImageClick={this.setActiveSlideShowImage}
              /> : null }
            <InsertLink
              isSimpleView={isSimpleView}
              activeSlideShowImage={activeSlideShowImage}
              isSlideShowInsertLinksMode={isSlideShowInsertLinksMode}
              slideShowImageDescription={slideShowImageDescription}
              tooltip={tooltip}
              linkText={linkText}
              url={url}
              previewImage={previewImage}
              onUrlChange={this.handleUrlChange}
              onLinkTextChange={this.handleLinkTextChange}
              onTooltipChange={this.handleTooltipChange}
              onOpenInNewWindowChange={this.handleOpenInNewWindowChange}
              onLinkStyleChange={this.handleLinkStyleChange}
              onSlideShowImageDescriptionChange={this.handleSlideShowImageDescriptionChange}
              openInNewWindow={openInNewWindow}
              isLinkTextEditable={isLinkTextEditable}
              baseDetailsUrlTemplate={baseDetailsUrlTemplate}
              baseRegistrationUrlTemplate={baseRegistrationUrlTemplate}
              eventsEndpoint={eventsEndpoint}
              pagesStructureEndpoint={pagesStructureEndpoint}
              isLinkTextSectionHidden={isLinkTextSectionHidden}
              isLinkStyleSectionHidden={isLinkStyleSectionHidden}
              isTooltipOptionHidden={isTooltipOptionHidden}
              isLinkOptionsHidden={isLinkOptionsHidden}
              linkStyles={linkStyles}
              linkStyle={linkStyle}
              linkStyleIframeSrc={linkStyleIframeSrc}
            />
          </div>
        </Modal.Content>
        <Modal.Footer>
          {this.getButtons()}
        </Modal.Footer>
      </Modal>
    );
  }
}

export default InsertLinkDialog;